import { useState, useEffect } from 'react';
import { useError } from '../../context/ErrorContext';
import { valuationFormService } from '../../services/valuationFormService';
import { Save, X } from 'lucide-react';

const ValuationFormContentManager = ({ setEditingValuationForm, updatePageLastUpdated }) => {
  const [pageContent, setPageContent] = useState(null);
  const [loading, setLoading] = useState(false);
  const { showError } = useError();

  useEffect(() => {
    const fetchContent = async () => {
      setLoading(true);
      try {
        const content = await valuationFormService.getValuationFormContent();
        setPageContent(content);
      } catch (error) {
        showError('Failed to load "Valuation Form" page content: ' + error.message, 'error');
        setPageContent(null);
      } finally {
        setLoading(false);
      }
    };
    fetchContent();
  }, [showError]);

  const handleChange = (section, field, value) => {
    setPageContent(prevContent => ({
      ...prevContent,
      [section]: {
        ...prevContent[section],
        [field]: value
      }
    }));
  };

  const handleSaveContent = async () => {
    try {
      setLoading(true);
      const updatedContent = await valuationFormService.createOrUpdateValuationFormContent(pageContent);
      setPageContent(updatedContent);
      updatePageLastUpdated('Valuation Form', updatedContent.lastUpdated);
      showError('"Valuation Form" page updated successfully!', 'success');
    } catch (error) {
      showError('Failed to save "Valuation Form" page content: ' + error.message, 'error');
    } finally {
      setLoading(false);
    }
  };

  if (!pageContent) {
    return <div className="flex items-center justify-center py-12 bg-white dark:bg-gray-800 rounded-lg shadow-lg dark:shadow-xl">
        <div className="flex flex-col items-center">
          <svg className="animate-spin h-8 w-8 text-accent dark:text-accent mb-3" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          <p className="text-lg text-gray-700 dark:text-gray-300">Loading Valuation Form...</p>
        </div>
      </div>;
  }

  return (
    <div className="bg-white dark:bg-gray-800 p-1 md:p-6 rounded-xl shadow-2xl dark:shadow-xl border border-gray-100 dark:border-gray-700 animate-fade-in">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
          Edit "Valuation Form" Page
        </h2>
        <button
          onClick={() => setEditingValuationForm(false)}
          className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 transition"
        >
          <X size={24} />
        </button>
      </div>

      <div className="space-y-6">
        {/* Hero Section */}
        <div>
          <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">Hero Section</h3>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Heading</label>
              <textarea
                rows="2"
                className="w-full border border-gray-300 dark:border-gray-600 rounded-md shadow-sm p-2.5 text-gray-800 dark:text-gray-200 focus:ring-accent focus:border-accent dark:bg-gray-900 transition duration-200"
                value={pageContent.hero.heading}
                onChange={(e) => handleChange('hero', 'heading', e.target.value)}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Description</label>
              <textarea
                rows="3"
                className="w-full border border-gray-300 dark:border-gray-600 rounded-md shadow-sm p-2.5 text-gray-800 dark:text-gray-200 focus:ring-accent focus:border-accent dark:bg-gray-900 transition duration-200"
                value={pageContent.hero.description}
                onChange={(e) => handleChange('hero', 'description', e.target.value)}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Contact Phone</label>
              <input
                type="text"
                className="w-full border border-gray-300 dark:border-gray-600 rounded-md shadow-sm p-2.5 text-gray-800 dark:text-gray-200 focus:ring-accent focus:border-accent dark:bg-gray-900 transition duration-200"
                value={pageContent.hero.contact}
                onChange={(e) => handleChange('hero', 'contact', e.target.value)}
              />
            </div>
          </div>
        </div>

        {/* Form Section */}
        <div>
          <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">Form Section</h3>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Personal Info Heading</label>
              <input
                type="text"
                className="w-full border border-gray-300 dark:border-gray-600 rounded-md shadow-sm p-2.5 text-gray-800 dark:text-gray-200 focus:ring-accent focus:border-accent dark:bg-gray-900 transition duration-200"
                value={pageContent.form.personalInfoHeading}
                onChange={(e) => handleChange('form', 'personalInfoHeading', e.target.value)}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Property Info Heading</label>
              <input
                type="text"
                className="w-full border border-gray-300 dark:border-gray-600 rounded-md shadow-sm p-2.5 text-gray-800 dark:text-gray-200 focus:ring-accent focus:border-accent dark:bg-gray-900 transition duration-200"
                value={pageContent.form.propertyInfoHeading}
                onChange={(e) => handleChange('form', 'propertyInfoHeading', e.target.value)}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Submit Button Text</label>
              <input
                type="text"
                className="w-full border border-gray-300 dark:border-gray-600 rounded-md shadow-sm p-2.5 text-gray-800 dark:text-gray-200 focus:ring-accent focus:border-accent dark:bg-gray-900 transition duration-200"
                value={pageContent.form.submitButtonText}
                onChange={(e) => handleChange('form', 'submitButtonText', e.target.value)}
              />
            </div>
          </div>
        </div>
      </div>

      <div className="mt-8 flex justify-end">
        <button
          onClick={handleSaveContent}
          className="flex items-center px-6 py-2.5 bg-accent dark:bg-accent text-white rounded-lg shadow-md hover:bg-accent/80 dark:hover:bg-accent/80 transition duration-200 font-semibold"
          disabled={loading}
        >
          <Save className="w-4 h-4 mr-2" /> Save Changes
        </button>
      </div>
    </div>
  );
};

export default ValuationFormContentManager;
